import React, { useState } from 'react';
import { BookMarked, Save } from 'lucide-react';
import { DictionaryResponse } from '../services/dictionary';

interface ExpressionFormProps {
  initialWord: string;
  onSave: (data: DictionaryResponse) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

export default function ExpressionForm({ initialWord, onSave, onCancel, isSaving }: ExpressionFormProps) {
  const [type, setType] = useState('expression');
  const [word, setWord] = useState(initialWord);
  const [translation, setTranslation] = useState('');
  const [literalMeaning, setLiteralMeaning] = useState('');
  const [exampleSentence, setExampleSentence] = useState('');
  const [exampleTranslation, setExampleTranslation] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Create a mock DictionaryResponse that also includes our custom fields
    // This allows it to pass through WordDefinition or directly to addWord
    const customData: any = {
      word: word.trim(),
      phonetic: '',
      phonetics: [],
      meanings: [{
        partOfSpeech: type.replace('_', ' '),
        definitions: [{
          definition: translation,
          example: exampleSentence,
          synonyms: [],
          antonyms: []
        }]
      }],
      synonyms: [],
      examples: exampleSentence ? [exampleSentence] : [],
      definition: translation,
      audio: '',
      // Custom fields
      type,
      translation,
      literalMeaning,
      exampleTranslation,
      exampleSentence
    };

    onSave(customData);
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border border-purple-200 mt-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookMarked className="w-6 h-6 text-purple-600" />
          Create Custom Expression
        </h2>
        <p className="text-gray-600 mt-2 text-sm">
          The dictionary couldn't find this expression. Fill in the details below to save it to your vocabulary!
        </p>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg">
        <p className="text-sm text-blue-700">
          <strong>Tip:</strong> Many English expressions cannot be translated word by word! Their real meaning is often very different from the literal translation.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select 
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="expression">Expression</option>
            <option value="idiom">Idiom</option>
            <option value="phrasal_verb">Phrasal Verb</option>
            <option value="compound_word">Compound Word</option>
            <option value="single_word">Single Word</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">English Expression</label>
          <input
            type="text"
            required
            value={word}
            onChange={(e) => setWord(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            placeholder="e.g. give up"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Real meaning in Portuguese</label>
          <input
            type="text"
            required
            value={translation}
            onChange={(e) => setTranslation(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            placeholder="e.g. desistir"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Literal word-by-word meaning (Optional)</label>
          <input
            type="text"
            value={literalMeaning}
            onChange={(e) => setLiteralMeaning(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            placeholder="e.g. dar para cima"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Example sentence in English (Optional)</label>
          <textarea
            value={exampleSentence}
            onChange={(e) => setExampleSentence(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            placeholder="e.g. Don't give up!"
            rows={2}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Translation of the example sentence (Optional)</label>
          <textarea
            value={exampleTranslation}
            onChange={(e) => setExampleTranslation(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
            placeholder="e.g. Não desista!"
            rows={2}
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex-1 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
          >
            {isSaving ? (
               <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
               <>
                 <Save className="w-5 h-5" />
                 Save Custom Expression
               </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
